/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * QubePro uygulama kabuğu ortak navigasyon bileşeni.
 * Route sabitleri uydu ürünlerde farklı kapsamlarda olabileceği için erişimler opsiyonel tutulur.
 */
import { SidebarSection } from './SidebarSection';
import { SidebarMenuSearch, SIDEBAR_MENU_SEARCH_MIN_ITEMS, matchesSidebarMenuSearch } from './SidebarMenuSearch';
import React, { useState, useEffect, useLayoutEffect, useRef, useId } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { canShowExternalMenu, parseAppsNavigation } from '../../context/apps-navigation.policy';
import { useTranslation } from '../../hooks/useTranslation';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { Icon } from '../icons/Icon';
import { ROUTES } from '../../config/routes';
import { getApplicationCodeForApi, getAppStorageKeyPrefix } from '../../config/app.config';
import axios from '../../api/axios';
import { orderSidebarSections } from './sidebar-section-order';
import { usePageTitleContext } from './PageTitle';
import { tenantAdminAllows } from '../../utils/product-permission-context.utils';
import { getCompanyScopeSnapshot } from '../../utils/company-scope-lifecycle.utils';

const MANAGEMENT_STORAGE_KEY = `${getAppStorageKeyPrefix()}_management_open`;
const SETTINGS_STORAGE_KEY = `${getAppStorageKeyPrefix()}_settings_open`;
const DYNAMIC_SECTION_STORAGE_PREFIX = `${getAppStorageKeyPrefix()}_sidebar_section_open_`;

export interface SidebarNavProps {
    /** Host implements General/Security/Keys under the user settings route. */
    personalSettingsTabs?: boolean;
    /** Product-owned selectors on the same-origin /api/manifest; never an alternate navigation service. */
    manifestQuery?: Readonly<Record<string, string>>;
    isCollapsed?: boolean;
    closeMobileMenu?: () => void;
    selectedCompanyId?: string;
    /** Firma bağlamı kesinleşmeden company-scoped capability menüleri değerlendirilmez. */
    isCompanyContextLoading?: boolean;
    /** Server capability kararları dahil nihai navigasyon durumu hazır olduğunda kabuğu bilgilendirir. */
    onReadyChange?: (ready: boolean) => void;
    children?: React.ReactNode;
    /**
     * Gösterge Paneli ile diğer bölümler (Yönetim vb.) ARASINA enjekte edilen
     * isteğe bağlı içerik. Ürüne özel ek navigasyon (ör. DMS klasör ağacı) için.
     * Geçilmezse hiçbir şey render edilmez → diğer ürünler etkilenmez.
     */
    afterDashboard?: React.ReactNode;
}

interface SidebarNavItem {
    code: string;
    path: string;
    icon: string;
    label: string;
    displayOrder: number;
}

interface SidebarNavSection {
    id: string;
    labelKey: string;
    presentation: 'section' | 'inline';
    items: SidebarNavItem[];
}

interface ManifestItem {
    code: string;
    name?: Record<string, string>;
    urlPattern: string;
    openInPanel?: boolean;
    icon?: string;
    displayOrder?: number;
    navCategory?: string;
    /** Inline sections render their items as top-level links without a collapsible category heading. */
    navPresentation?: 'section' | 'inline';
    permissionCode?: string;
    /** Explicit alternative existing capabilities; the server still authorizes every operation. */
    permissionCodes?: readonly string[];
    adminOnly?: boolean;
    /** Authenticated, selected-company scoped endpoint returning standard data.enabled. Failure is fail-closed. */
    capabilityUrl?: string;
}

const routeMap = ROUTES as unknown as Record<string, unknown>;
const getRoute = (key: string) => {
    const value = routeMap[key];
    return typeof value === 'string' ? value : null;
};
const routeList = (keys: string[]) => keys.map(getRoute).filter((p): p is string => Boolean(p));

const SECTION_LABELS: Record<string, { labelKey: string; fallback: string; icon: string }> = {
    dashboard: { labelKey: 'sidebar.dashboard', fallback: 'Gösterge Paneli', icon: 'dashboard' },
    management: { labelKey: 'sidebar.management', fallback: 'Yönetim', icon: 'admin' },
    customerManagement: { labelKey: 'sidebar.customerManagement', fallback: 'Müşteri Yönetimi', icon: 'users' },
    sales: { labelKey: 'sidebar.sales', fallback: 'Satış', icon: 'business' },
    engagement: { labelKey: 'sidebar.engagement', fallback: 'Etkileşim', icon: 'activity' },
    settings: { labelKey: 'sidebar.settings', fallback: 'Ayarlar', icon: 'settings' },
    support: { labelKey: 'sidebar.support', fallback: 'Destek', icon: 'support' },
    analytics: { labelKey: 'sidebar.analytics', fallback: 'Analitik', icon: 'chart' },
    agents: { labelKey: 'sidebar.agents', fallback: 'Agentlar', icon: 'aiWorker' },
    jobs: { labelKey: 'sidebar.jobs', fallback: 'İşler', icon: 'schedule' },
    tasks: { labelKey: 'sidebar.tasks', fallback: 'Görevler', icon: 'schedule' },
    processes: { labelKey: 'sidebar.processes', fallback: 'Süreçler', icon: 'organization' },
    advanced: { labelKey: 'sidebar.advanced', fallback: 'Gelişmiş', icon: 'settings' },
    learning: { labelKey: 'sidebar.learning', fallback: 'Öğrenme', icon: 'book' },
    outputs: { labelKey: 'sidebar.outputs', fallback: 'Çıktılar', icon: 'file-text' },
    channel: { labelKey: 'sidebar.channel', fallback: 'Kanal', icon: 'users' },
    admin: { labelKey: 'sidebar.admin', fallback: 'Yönetici', icon: 'settings' },
};

// Product locales may omit a generic shell heading. Keep that fallback in the
// shared shell so a satellite's missing translation never turns into a Turkish
// heading in another language.
const SECTION_HEADING_FALLBACKS: Record<string, Record<string, string>> = {
    admin: { tr: 'Yönetici', en: 'Administration', ar: 'الإدارة', de: 'Administration', fr: 'Administration', es: 'Administración', it: 'Amministrazione', ru: 'Администрирование', zh: '管理', ja: '管理' },
    management: { tr: 'Yönetim', en: 'Management', ar: 'الإدارة', de: 'Verwaltung', fr: 'Gestion', es: 'Gestión', it: 'Gestione', ru: 'Управление', zh: '管理', ja: '管理' },
    settings: { tr: 'Ayarlar', en: 'Settings', ar: 'الإعدادات', de: 'Einstellungen', fr: 'Paramètres', es: 'Configuración', it: 'Impostazioni', ru: 'Настройки', zh: '设置', ja: '設定' },
    support: { tr: 'Destek', en: 'Support', ar: 'الدعم', de: 'Support', fr: 'Assistance', es: 'Soporte', it: 'Supporto', ru: 'Поддержка', zh: '支持', ja: 'サポート' },
    outputs: { tr: 'Çıktılar', en: 'Outputs', ar: 'المخرجات', de: 'Ausgaben', fr: 'Sorties', es: 'Resultados', it: 'Output', ru: 'Результаты', zh: '输出', ja: '出力' },
};

const CUSTOM_FIELDS_MENU_LABELS: Record<string, string> = {
    tr: 'Ek Alanlar',
    en: 'Custom Fields',
    ar: 'الحقول المخصصة',
    de: 'Benutzerdefinierte Felder',
    fr: 'Champs personnalisés',
    es: 'Campos personalizados',
    it: 'Campi personalizzati',
    ru: 'Пользовательские поля',
    zh: '自定义字段',
    ja: 'カスタム項目',
};
const NAVIGATION_LOAD_COPY: Record<string, { error: string; retry: string }> = {
    tr: { error: 'Menü yüklenemedi.', retry: 'Yeniden dene' },
    en: { error: 'Could not load the menu.', retry: 'Try again' },
    ar: { error: 'تعذر تحميل القائمة.', retry: 'أعد المحاولة' },
    de: { error: 'Das Menü konnte nicht geladen werden.', retry: 'Erneut versuchen' },
    fr: { error: 'Impossible de charger le menu.', retry: 'Réessayer' },
    es: { error: 'No se pudo cargar el menú.', retry: 'Reintentar' },
    it: { error: 'Impossibile caricare il menu.', retry: 'Riprova' },
    ru: { error: 'Не удалось загрузить меню.', retry: 'Повторить' },
    zh: { error: '无法加载菜单。', retry: '重试' },
    ja: { error: 'メニューを読み込めませんでした。', retry: '再試行' },
};

function unwrapApiData<T>(payload: unknown): T | null {
    if (!payload || typeof payload !== 'object') return null;
    const root = payload as Record<string, unknown>;
    const data = root.data;
    if (data && typeof data === 'object' && 'data' in (data as Record<string, unknown>)) {
        return (data as Record<string, unknown>).data as T;
    }
    return (data ?? payload) as T;
}

function getLocalizedName(name: Record<string, string> | undefined, locale: string, fallback: string): string {
    const lang = (locale || 'tr').toLowerCase().split('-')[0];
    return name?.[lang] ?? name?.tr ?? name?.en ?? fallback;
}

function normalizeLinkPath(path: string): string {
    const value = String(path || '').trim();
    if (!value) return '/';
    const routeValues = new Set(
        Object.values(routeMap)
            .filter((route): route is string => typeof route === 'string')
            .map((route) => route.replace(/\/+$/, '') || '/')
    );
    const stripKnownBase = (candidate: string) => {
        const [pathOnly, suffix = ''] = candidate.split(/(?=[?#])/, 2);
        const currentBase = window.location.pathname.split('/').filter(Boolean)[0];
        const configuredBase = String(import.meta.env.VITE_BASE_PATH || '').replace(/^\/+|\/+$/g, '');
        const appCodeBase = getApplicationCodeForApi().toLowerCase();
        const baseCandidates = [configuredBase, appCodeBase, currentBase].filter(Boolean);
        for (const base of baseCandidates) {
            if (!pathOnly.startsWith(`/${base}/`)) continue;
            const stripped = pathOnly.slice(base.length + 1) || '/';
            const normalized = stripped.replace(/\/+$/, '') || '/';
            if (routeValues.has(normalized) || base === configuredBase || base === appCodeBase) return `${stripped}${suffix}`;
        }
        return candidate;
    };
    try {
        const url = new URL(value, window.location.origin);
        if (url.origin === window.location.origin) {
            return stripKnownBase(`${url.pathname}${url.search}${url.hash}`);
        }
    } catch {
        // Relative route; normalize below.
    }
    return stripKnownBase(value.startsWith('/') ? value : `/${value}`);
}

function routeWithoutQuery(path: string): string {
    return normalizeLinkPath(path).split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
}

function isMenuCompatibleWithRoutes(menu: SidebarNavSection[] | null): boolean {
    if (!menu || menu.length === 0) return false;
    const allItems = menu.flatMap((section) => section.items);
    if (allItems.length === 0) return false;

    // Satellite products own their application routes outside the shared Core
    // ROUTES table. Their same-origin manifest is therefore the route contract.
    if (!isCoreApplication()) return true;

    const routes = new Set(
        Object.values(routeMap)
            .filter((value): value is string => typeof value === 'string')
            .map((value) => routeWithoutQuery(value))
    );
    if (routes.size === 0) return true;
    return allItems.every((item) => {
        const itemPath = routeWithoutQuery(item.path);
        return routes.has(itemPath) || itemPath === '/' || itemPath.startsWith('/core/');
    });
}

function getSectionLabel(sectionId: string, explicitLabelKey: string | undefined, t: any, locale: string): string {
    const labelKey = explicitLabelKey || SECTION_LABELS[sectionId]?.labelKey || '';
    const sidebarKey = labelKey.startsWith('sidebar.') ? labelKey.slice('sidebar.'.length) : sectionId;
    const language = (locale || 'tr').toLowerCase().split('-')[0];
    return t.sidebar?.[sidebarKey] ?? SECTION_HEADING_FALLBACKS[sectionId]?.[language]
        ?? SECTION_HEADING_FALLBACKS[sectionId]?.en ?? SECTION_LABELS[sectionId]?.fallback ?? sidebarKey;
}

function getSectionIcon(sectionId: string): string {
    return SECTION_LABELS[sectionId]?.icon ?? 'folder';
}

function shouldRenderSectionInline(section: SidebarNavSection): boolean {
    if (section.presentation === 'inline') return true;
    if (section.items.length !== 1) return false;
    return !['dashboard', 'management', 'settings', 'admin'].includes(section.id);
}

function isCoreApplication(): boolean {
    return getApplicationCodeForApi() === 'CORE';
}

function isActivityLogMenuItem(item: ManifestItem): boolean {
    const code = String(item.code || '').toLowerCase();
    const permissionCode = String(item.permissionCode || '').toLowerCase();
    const path = String(item.urlPattern || '').toLowerCase();
    return (
        code.includes('activitylog') ||
        code.includes('activity-log') ||
        permissionCode.includes('activitylog') ||
        permissionCode.includes('activity-log') ||
        path.includes('/activity-logs') ||
        path.includes('/activitylogs')
    );
}

function buildMenuFromManifest(manifest: { menus?: ManifestItem[]; items?: ManifestItem[] } | null, locale: string, user: any, externalPermissions: string[] = [], personalSettingsTabs = false): SidebarNavSection[] | null {
    const menus = manifest?.menus ?? manifest?.items ?? [];
    if (!Array.isArray(menus) || menus.length === 0) return null;

    const userPermissions = Array.isArray(user?.permissions) ? user.permissions.map((p: unknown) => String(p)) : [];
    const isAdmin = user?.userKind === 'INTERNAL' && Boolean(user?.isAdmin);
    const currentScope = getCompanyScopeSnapshot();
    const hasTenantPermission = (code: string) => !currentScope.blocked && tenantAdminAllows(user, code,
        { appCode: getApplicationCodeForApi(), companyId: currentScope.companyId });
    const sections = new Map<string, SidebarNavSection>();

    const canShow = (item: ManifestItem) => {
        const required = item.permissionCodes === undefined ? [] : item.permissionCodes;
        if (!Array.isArray(required) || required.length > 16 || required.some(code =>
            typeof code !== 'string' || !code.trim() || code.length > 128)) return false;
        const codes = [...new Set([...(item.permissionCode ? [item.permissionCode] : []), ...required])];
        if (item.permissionCodes !== undefined && codes.length === 0) return false;
        if (!isCoreApplication() && isActivityLogMenuItem(item)) return false;
        // Legacy adminOnly metadata must not veto the current company's exact
        // permission or server-bound tenant management scope. Role IDs alone
        // never prove either authority; permissionless admin menus stay closed.
        if (item.adminOnly && !isAdmin && !codes.some(code => userPermissions.includes(code) || hasTenantPermission(code))) return false;
        if (user?.userKind !== 'INTERNAL') return codes.some(code => canShowExternalMenu(user?.userKind, code, userPermissions, externalPermissions));
        if (codes.length === 0 || isAdmin) return true;
        return userPermissions.includes('*') || codes.some(code => userPermissions.includes(code) || hasTenantPermission(code));
    };

    const visibleMenus = menus.filter((item) => item && !item.openInPanel && item.urlPattern && !item.urlPattern.includes('?') && canShow(item));
    const userSettingsRoute = getRoute('SETTINGS_USER');
    const securitySettingsRoute = getRoute('SETTINGS_SECURITY');
    const hasPersonalSettings = personalSettingsTabs && typeof userSettingsRoute === 'string' && userSettingsRoute.length > 0 && visibleMenus.some(item => routeWithoutQuery(item.urlPattern) === routeWithoutQuery(userSettingsRoute));
    // Security remains a supported deep link inside the personal settings tabs.
    // Collapse only the duplicate link, after the normal permission filter.
    visibleMenus
        .filter(item => !hasPersonalSettings || !securitySettingsRoute || routeWithoutQuery(item.urlPattern) !== routeWithoutQuery(securitySettingsRoute))
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
        .forEach((item) => {
            const sectionId = item.navCategory || 'management';
            const section = sections.get(sectionId) ?? {
                id: sectionId,
                labelKey: SECTION_LABELS[sectionId]?.labelKey ?? `sidebar.${sectionId}`,
                presentation: item.navPresentation === 'inline' ? 'inline' : 'section',
                items: [],
            };
            if (item.navPresentation === 'inline') section.presentation = 'inline';
            section.items.push({
                code: item.code,
                path: normalizeLinkPath(item.urlPattern),
                icon: item.icon || getSectionIcon(sectionId),
                label: getLocalizedName(item.name, locale, item.code),
                displayOrder: item.displayOrder ?? 0,
            });
            sections.set(sectionId, section);
        });

    const visibleSections = Array.from(sections.values()).filter((section) => section.items.length > 0);
    const withSharedSettings = isAdmin || hasTenantPermission('customField:read') || (user?.userKind === 'INTERNAL' && userPermissions.includes('customField:read'))
        ? ensureCustomFieldsMenu(visibleSections, locale)
        : visibleSections;
    const result = orderSidebarSections(withSharedSettings);
    return result.length > 0 ? result : null;
}

async function filterMenusByServerCapability(
    manifest: { menus?: ManifestItem[]; items?: ManifestItem[] } | null,
    selectedCompanyId?: string,
): Promise<{ menus?: ManifestItem[]; items?: ManifestItem[] } | null> {
    if (!manifest) return null;
    const source = manifest.menus ?? manifest.items ?? [];
    const decisions = await Promise.all(source.map(async (item) => {
        if (!item.capabilityUrl) return item;
        try {
            if (!selectedCompanyId) return null;
            const response = await axios.get(item.capabilityUrl, {
                params: { companyId: selectedCompanyId },
            });
            const capability = unwrapApiData<{ enabled?: boolean }>(response.data);
            return capability?.enabled === true ? item : null;
        } catch {
            return null;
        }
    }));
    const filtered = decisions.filter((item): item is ManifestItem => item != null);
    return manifest.menus ? { ...manifest, menus: filtered } : { ...manifest, items: filtered };
}

function ensureCustomFieldsMenu(sections: SidebarNavSection[], locale: string): SidebarNavSection[] {
    const customFieldsRoute = getRoute('CUSTOM_FIELDS');
    if (!customFieldsRoute) return sections;

    const customFieldsPath = normalizeLinkPath(customFieldsRoute);
    const customFieldsBase = routeWithoutQuery(customFieldsPath);
    const customFieldsIdentity = `${getApplicationCodeForApi().toLowerCase()}:customField:view`;
    const alreadyExists = sections.some((section) =>
        section.items.some((item) => routeWithoutQuery(item.path) === customFieldsBase
            || item.code === customFieldsIdentity)
    );
    if (alreadyExists) return sections;

    const next = sections.map((section) => ({ ...section, items: [...section.items] }));
    let settingsSection = next.find((section) => section.id === 'settings');
    if (!settingsSection) {
        settingsSection = {
            id: 'settings',
            labelKey: SECTION_LABELS.settings.labelKey,
            presentation: 'section',
            items: [],
        };
        next.push(settingsSection);
    }

    const translationsRoute = getRoute('TRANSLATIONS');
    const translationsBase = translationsRoute ? routeWithoutQuery(translationsRoute) : null;
    const translationIndex = translationsBase
        ? settingsSection.items.findIndex((item) => routeWithoutQuery(item.path) === translationsBase)
        : -1;
    const displayOrder = translationIndex >= 0
        ? settingsSection.items[translationIndex].displayOrder - 0.1
        : 89.9;
    const item: SidebarNavItem = {
        code: `${getApplicationCodeForApi().toLowerCase()}_custom_fields`,
        path: customFieldsPath,
        icon: 'tag',
        label: getLocalizedName(CUSTOM_FIELDS_MENU_LABELS, locale, CUSTOM_FIELDS_MENU_LABELS.en),
        displayOrder,
    };

    if (translationIndex >= 0) {
        settingsSection.items.splice(translationIndex, 0, item);
    } else {
        settingsSection.items.push(item);
        settingsSection.items.sort((a, b) => a.displayOrder - b.displayOrder);
    }

    return next;
}

export const SidebarNav: React.FC<SidebarNavProps> = ({
    manifestQuery,
    personalSettingsTabs = false,
    isCollapsed = false,
    closeMobileMenu = () => undefined,
    selectedCompanyId,
    isCompanyContextLoading = false,
    onReadyChange,
    children,
    afterDashboard,
}) => {
    const { language, user } = useAuth();
    const location = useLocation();
    const t = useTranslation(language);
    const setPageTitleItems = usePageTitleContext()?.setItems;
    const { canViewManagement, canViewSettings, canViewSystemSettings, canViewUserSettings, canAccessPermission } = useRoleAccess();

    const [isManagementOpen, setIsManagementOpen] = useState(() => {
        const saved = localStorage.getItem(MANAGEMENT_STORAGE_KEY);
        return saved !== 'false';
    });

    const [isSettingsOpen, setIsSettingsOpen] = useState(() => {
        const saved = localStorage.getItem(SETTINGS_STORAGE_KEY);
        return saved !== 'false';
    });
    const [menuSearch, setMenuSearch] = useState('');
    const [menuReload, setMenuReload] = useState(0);
    const navigationCopy = NAVIGATION_LOAD_COPY[(language || 'en').toLowerCase().split(/[-_]/)[0]] ?? NAVIGATION_LOAD_COPY.en;

    const permissionContext = Array.isArray(user?.permissions)
        ? user.permissions.map((permission: unknown) => String(permission)).sort().join(',')
        : '';
    const navigationUser = user as unknown as Record<string, unknown> | null;
    const manifestQueryKey = JSON.stringify(Object.entries(manifestQuery ?? {}).sort(([a], [b]) => a.localeCompare(b)));
    const navContextKey = JSON.stringify([
        language || 'tr',
        selectedCompanyId || '',
        navigationUser?.id ?? navigationUser?.userId ?? navigationUser?.sub ?? '',
        Boolean(user?.isAdmin),
        user?.userKind, user?.tenantId, user?.membershipId, user?.authVersion,
        user?.membershipVersion, user?.tenantAdminScope,
        permissionContext,
        manifestQueryKey,
        personalSettingsTabs,
        menuReload,
    ]);
    const [navState, setNavState] = useState<{
        contextKey: string;
        resolved: boolean;
        menu: SidebarNavSection[] | null;
        error: boolean;
    }>({ contextKey: '', resolved: false, menu: null, error: false });
    const isNavMenuResolved = !isCompanyContextLoading
        && navState.resolved
        && navState.contextKey === navContextKey;
    const navMenu = isNavMenuResolved ? navState.menu : null;
    useEffect(() => {
        // Reuse the already localized, permission/capability-filtered menu. No second request.
        const sharedTitles: Array<[string, string | undefined]> = [
            ['SEARCH', t.sidebar?.search ?? t.common?.search],
            ['NOTIFICATIONS', t.sidebar?.notifications ?? t.common?.notificationsTitle ?? t.notifications?.title],
            ['SETTINGS_SYSTEM', t.settings?.systemSettings],
            ['SETTINGS_COMPANY', t.settings?.companySettings],
            ['SETTINGS_USER', t.settings?.userSettings],
            ['SETTINGS_SECURITY', personalSettingsTabs ? t.settings?.userSettings : t.settings?.security],
            ['SETTINGS', t.sidebar?.settings],
            ['TRANSLATIONS', t.sidebar?.translations],
            ['CUSTOM_FIELDS', t.customFields?.title],
        ];
        setPageTitleItems?.([
            ...(navMenu?.flatMap(section => section.items) ?? []),
            ...sharedTitles.flatMap(([key, label]) => {
                const path = getRoute(key);
                return path && label ? [{ path, label }] : [];
            }),
        ]);
    }, [navMenu, t, setPageTitleItems, personalSettingsTabs]);
    const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
    const navRef = useRef<HTMLElement | null>(null);
    const sectionIdPrefix = useId();

    useLayoutEffect(() => {
        navRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        setMenuSearch('');
    }, [location.pathname]);

    useEffect(() => {
        if (isCollapsed) setMenuSearch('');
    }, [isCollapsed]);

    useEffect(() => {
        onReadyChange?.(isNavMenuResolved);
    }, [isNavMenuResolved, onReadyChange]);

    useEffect(() => {
        let cancelled = false;
        const locale = language || 'tr';

        const loadMenu = async () => {
            if (isCompanyContextLoading) return;
            let menu: SidebarNavSection[] | null = null;
            let failed = false;
            try {
                const query = Object.fromEntries(JSON.parse(manifestQueryKey)) as Record<string, string>;
                if (Object.keys(query).length > 8 || Object.entries(query).some(([key, value]) =>
                    !/^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(key) || typeof value !== 'string' || value.length > 256)) {
                    throw new Error('Invalid manifest selector');
                }
                const manifestRes = await axios.get('/api/manifest', Object.keys(query).length ? { params: query } : undefined);
                const manifest = unwrapApiData<{ menus?: ManifestItem[]; items?: ManifestItem[] }>(manifestRes.data);
                const capabilityFilteredManifest = await filterMenusByServerCapability(manifest, selectedCompanyId);
                let externalPermissions: string[] = [];
                if (user?.userKind === 'EXTERNAL') {
                    const result = await axios.get('/api/apps/navigation');
                    const decision = parseAppsNavigation(result.data?.data ?? result.data);
                    externalPermissions = decision?.userKind === 'EXTERNAL'
                        ? decision.applications.find(app => app.code === getApplicationCodeForApi())?.permissions ?? [] : [];
                }
                menu = buildMenuFromManifest(capabilityFilteredManifest, locale, user, externalPermissions, personalSettingsTabs);
            } catch {
                menu = null;
                failed = true;
            }

            if (!isMenuCompatibleWithRoutes(menu)) menu = null;

            if (!cancelled) {
                setNavState({
                    contextKey: navContextKey,
                    resolved: true,
                    menu,
                    error: failed,
                });
            }
        };

        loadMenu();
        return () => { cancelled = true; };
    }, [isCompanyContextLoading, navContextKey]);

    useEffect(() => {
        const managementPaths = routeList(['ROLES', 'ORGANIZATIONS', 'USERS', 'COMPANIES', 'GROUPS', 'EMAIL_ACCOUNTS', 'AI_PROVIDERS', 'ACTIVITY_LOGS']);
        const currentPath = location.pathname.replace(/\/+$/, '') || '/';
        const isOnManagementPage = managementPaths.some((p) => {
            const route = p.replace(/\/+$/, '') || '/';
            return currentPath === route || currentPath.startsWith(route + '/');
        });

        if (isOnManagementPage) {
            const currentState = localStorage.getItem(MANAGEMENT_STORAGE_KEY) === 'true';
            if (!currentState) {
                setIsManagementOpen(true);
                localStorage.setItem(MANAGEMENT_STORAGE_KEY, 'true');
            }
        }
    }, [location.pathname]);

    useEffect(() => {
        const settingsPaths = routeList(['SETTINGS_SYSTEM', 'SETTINGS_USER', 'SETTINGS_SECURITY', 'CUSTOM_FIELDS', 'TRANSLATIONS']);
        const settingsRoute = getRoute('SETTINGS');
        const currentPath = location.pathname.replace(/\/+$/, '') || '/';
        const isOnSettingsPage = settingsPaths.some((p) => {
            const route = p.replace(/\/+$/, '') || '/';
            return currentPath === route || currentPath.startsWith(route + '/');
        }) || Boolean(settingsRoute && currentPath === settingsRoute);

        if (isOnSettingsPage) {
            const currentState = localStorage.getItem(SETTINGS_STORAGE_KEY) === 'true';
            if (!currentState) {
                setIsSettingsOpen(true);
                localStorage.setItem(SETTINGS_STORAGE_KEY, 'true');
            }
        }
    }, [location.pathname]);

    const handleManagementToggle = () => {
        const newState = !isManagementOpen;
        setIsManagementOpen(newState);
        localStorage.setItem(MANAGEMENT_STORAGE_KEY, String(newState));
    };

    const handleSettingsToggle = () => {
        const newState = !isSettingsOpen;
        setIsSettingsOpen(newState);
        localStorage.setItem(SETTINGS_STORAGE_KEY, String(newState));
    };

    const handleDynamicSectionToggle = (sectionId: string) => {
        if (sectionId === 'management') {
            handleManagementToggle();
            return;
        }
        if (sectionId === 'settings') {
            handleSettingsToggle();
            return;
        }

        setOpenSections((prev) => {
            const newState = !(prev[sectionId] ?? true);
            try {
                localStorage.setItem(`${DYNAMIC_SECTION_STORAGE_PREFIX}${sectionId}`, String(newState));
            } catch {
                // Ignore storage errors; menu remains usable for the current session.
            }
            return { ...prev, [sectionId]: newState };
        });
    };

    useEffect(() => {
        if (!navMenu) return;

        setOpenSections((prev) => {
            const next = { ...prev };
            for (const section of navMenu) {
                if (section.id === 'dashboard' || section.id === 'management' || section.id === 'settings') continue;
                if (section.id in next) continue;
                try {
                    const saved = localStorage.getItem(`${DYNAMIC_SECTION_STORAGE_PREFIX}${section.id}`);
                    next[section.id] = saved === null ? true : saved !== 'false';
                } catch {
                    next[section.id] = true;
                }
            }
            return next;
        });
    }, [navMenu]);

    const isActive = (path: string) => {
        const currentPath = routeWithoutQuery(location.pathname);
        const route = routeWithoutQuery(path);
        return currentPath === route || currentPath.startsWith(route + '/');
    };

    const isDashboardActive = () => {
        const path = location.pathname.replace(/\/+$/, '') || '/';
        return path === getRoute('DASHBOARD');
    };

    const hasActiveManagementItem = () => {
        const managementPaths = routeList(['COMPANIES', 'ORGANIZATIONS', 'ROLES', 'USERS', 'GROUPS', 'EMAIL_ACCOUNTS', 'AI_PROVIDERS', 'ACTIVITY_LOGS']);
        return managementPaths.some(p => isActive(p));
    };

    const hasActiveSettingsItem = () => {
        return routeList(['SETTINGS_SYSTEM', 'SETTINGS_USER', 'SETTINGS_SECURITY', 'CUSTOM_FIELDS', 'TRANSLATIONS']).some(p => isActive(p));
    };

    const dashboardRoute = getRoute('DASHBOARD');
    const managementRoutes = {
        companies: getRoute('COMPANIES'),
        organizations: getRoute('ORGANIZATIONS'),
        roles: getRoute('ROLES'),
        users: getRoute('USERS'),
        groups: getRoute('GROUPS'),
        emailAccounts: canAccessPermission('email:read') ? getRoute('EMAIL_ACCOUNTS') : '',
        aiProviders: canAccessPermission('aiProvider:read') ? getRoute('AI_PROVIDERS') : '',
        // Aktivite logları merkezidir: okuma/raporlama yalnızca Core UI'da. Uydularda menü gizli.
        activityLogs: canAccessPermission('activityLog:read') && isCoreApplication() ? getRoute('ACTIVITY_LOGS') : '',
    };
    const settingsRoutes = {
        system: getRoute('SETTINGS_SYSTEM'),
        user: getRoute('SETTINGS_USER'),
        security: getRoute('SETTINGS_SECURITY'),
        customFields: getRoute('CUSTOM_FIELDS'),
        translations: getRoute('TRANSLATIONS'),
    };

    const menuItemCount = navMenu?.reduce((count, section) => count + section.items.length, 0) ?? 0;
    const activeSearch = !isCollapsed && menuItemCount >= SIDEBAR_MENU_SEARCH_MIN_ITEMS ? menuSearch.trim() : '';
    const displayMenu = activeSearch && navMenu
        ? navMenu.map((section) => {
            const label = getSectionLabel(section.id, section.labelKey, t, language);
            return {
                ...section,
                items: section.items.filter((item) => matchesSidebarMenuSearch(activeSearch, label, item.label)),
            };
        }).filter((section) => section.items.length > 0)
        : navMenu ?? [];
    const resultCount = displayMenu.reduce((count, section) => count + section.items.length, 0);

    if (children) {
        return <nav ref={navRef} className="sidebar-nav" data-tour-id="primary-navigation">{children}</nav>;
    }

    return (
        <nav ref={navRef} className="sidebar-nav" data-tour-id="primary-navigation">
            {!isNavMenuResolved ? (
                <div className="nav-item" role="status" aria-live="polite" aria-label={t.common.loading}>
                    <span className="nav-icon"><Icon name="loading" size={20} /></span>
                    {!isCollapsed && <span>{t.common.loading}</span>}
                </div>
            ) : navMenu ? (
              <>
                <SidebarMenuSearch value={menuSearch} onChange={setMenuSearch} itemCount={menuItemCount}
                    resultCount={resultCount} isCollapsed={isCollapsed} locale={language} />
                {!activeSearch && !navMenu.some((s) => s.id === 'dashboard' && s.items.length > 0) && afterDashboard}
                {displayMenu.map((section) => {
                    const sectionLabel = getSectionLabel(section.id, section.labelKey, t, language);
                    const isOpen = activeSearch ? true : section.id === 'management'
                        ? isManagementOpen
                        : section.id === 'settings'
                            ? isSettingsOpen
                            : (openSections[section.id] ?? true);
                    const onToggle = () => activeSearch ? setMenuSearch('') : handleDynamicSectionToggle(section.id);
                    const hasActive = section.items.some((item) => isActive(item.path));

                    if (section.id === 'dashboard' && section.items.length > 0) {
                        return (
                            <React.Fragment key="dashboard">
                                {section.items.map((item) => (
                                    <Link
                                        key={item.code}
                                        to={item.path}
                                        className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                                        title={item.label}
                                        onClick={closeMobileMenu}
                                    >
                                        <span className="nav-icon"><Icon name={item.icon as any} size={20} /></span>
                                        {!isCollapsed && <span>{item.label}</span>}
                                    </Link>
                                ))}
                                {!activeSearch && afterDashboard}
                            </React.Fragment>
                        );
                    }

                    if (section.items.length === 0) return null;

                    if (shouldRenderSectionInline(section)) {
                        return (
                            <React.Fragment key={section.id}>
                                {section.items.map((item) => (
                                    <Link
                                        key={item.code}
                                        to={item.path}
                                        className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                                        title={item.label}
                                        onClick={closeMobileMenu}
                                        {...(item.code === 'core_roles' && { 'data-qa': 'nav-item-roles' })}
                                    >
                                        <span className="nav-icon"><Icon name={item.icon as any} size={20} /></span>
                                        {!isCollapsed && (
                                            <span {...(item.code === 'core_roles' && { 'data-qa': 'nav-label-roles' })}>{item.label}</span>
                                        )}
                                    </Link>
                                ))}
                            </React.Fragment>
                        );
                    }

                    return (
                        <SidebarSection key={section.id} id={`${sectionIdPrefix}-${section.id}`}
                            label={sectionLabel} isCollapsed={isCollapsed} isOpen={isOpen} hasActiveChild={hasActive}
                            onToggle={onToggle ?? (() => undefined)}
                            icon={<Icon name={getSectionIcon(section.id)} size={20} />}
                            arrow={isOpen ? <Icon name="expandMore" size={16} /> : <Icon name="chevronRight" size={16} />}>
                                {section.items.map((item) => (
                                    <Link
                                        key={item.code}
                                        to={item.path}
                                        className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                                        title={item.label}
                                        onClick={closeMobileMenu}
                                        {...(item.code === 'core_roles' && { 'data-qa': 'nav-item-roles' })}
                                    >
                                        <span className="nav-icon"><Icon name={item.icon as any} size={20} /></span>
                                        {!isCollapsed && (
                                            <span {...(item.code === 'core_roles' && { 'data-qa': 'nav-label-roles' })}>{item.label}</span>
                                        )}
                                    </Link>
                                ))}
                        </SidebarSection>
                    );
                })}
              </>
            ) : !isCoreApplication() || manifestQueryKey !== '[]' ? (
                navState.error ? <div className="nav-item" role="alert">
                    {!isCollapsed && <span>{navigationCopy.error}</span>}
                    <button type="button" className="btn-secondary" onClick={() => setMenuReload(value => value + 1)}
                        aria-label={navigationCopy.retry}>{navigationCopy.retry}</button>
                </div> : <div className="nav-item" role="status">{!isCollapsed && t.common.noPermission}</div>
            ) : (
                <>
                    {canAccessPermission('dashboard:read') && dashboardRoute && (
                        <Link to={dashboardRoute} className={`nav-item ${isDashboardActive() ? 'active' : ''}`} title={t.sidebar.dashboard} onClick={closeMobileMenu}>
                            <span className="nav-icon"><Icon name="dashboard" size={20} /></span>
                            {!isCollapsed && <span>{t.sidebar.dashboard}</span>}
                        </Link>
                    )}
                    {afterDashboard}
                    {canViewManagement && (
                        <SidebarSection id={`${sectionIdPrefix}-management`} label={t.sidebar.management}
                            isCollapsed={isCollapsed} isOpen={isManagementOpen} hasActiveChild={hasActiveManagementItem()}
                            onToggle={handleManagementToggle} icon={<Icon name="admin" size={20} />}
                            arrow={isManagementOpen ? <Icon name="expandMore" size={16} /> : <Icon name="chevronRight" size={16} />}>
                                {canAccessPermission('company:read') && managementRoutes.companies && <Link to={managementRoutes.companies} className={`nav-item ${isActive(managementRoutes.companies) ? 'active' : ''}`} title={t.sidebar.companies} onClick={closeMobileMenu}><span className="nav-icon"><Icon name="companies" size={20} /></span>{!isCollapsed && <span>{t.sidebar.companies}</span>}</Link>}
                                {canAccessPermission('organization:read') && managementRoutes.organizations && <Link to={managementRoutes.organizations} className={`nav-item ${isActive(managementRoutes.organizations) ? 'active' : ''}`} title={t.organizations.title} onClick={closeMobileMenu}><span className="nav-icon"><Icon name="organization" size={20} /></span>{!isCollapsed && <span>{t.organizations.title}</span>}</Link>}
                                {canAccessPermission('role:read') && managementRoutes.roles && <Link to={managementRoutes.roles} className={`nav-item ${isActive(managementRoutes.roles) ? 'active' : ''}`} title={t.sidebar.roles} onClick={closeMobileMenu} data-qa="nav-item-roles"><span className="nav-icon"><Icon name="roles" size={20} /></span>{!isCollapsed && <span data-qa="nav-label-roles">{t.sidebar.roles}</span>}</Link>}
                                {canAccessPermission('user:read') && managementRoutes.users && <Link to={managementRoutes.users} className={`nav-item ${isActive(managementRoutes.users) ? 'active' : ''}`} title={t.sidebar.users} onClick={closeMobileMenu}><span className="nav-icon"><Icon name="users" size={20} /></span>{!isCollapsed && <span>{t.sidebar.users}</span>}</Link>}
                                {canAccessPermission('user:read') && managementRoutes.groups && <Link to={managementRoutes.groups} className={`nav-item ${isActive(managementRoutes.groups) ? 'active' : ''}`} title={t.users.groups} onClick={closeMobileMenu}><span className="nav-icon"><Icon name="organization" size={20} /></span>{!isCollapsed && <span>{t.users.groups}</span>}</Link>}
                                {managementRoutes.emailAccounts && <Link to={managementRoutes.emailAccounts} className={`nav-item ${isActive(managementRoutes.emailAccounts) ? 'active' : ''}`} title={t.sidebar.emailAccounts} onClick={closeMobileMenu}><span className="nav-icon"><Icon name="email" size={20} /></span>{!isCollapsed && <span>{t.sidebar.emailAccounts}</span>}</Link>}
                                {canAccessPermission('aiProvider:read') && managementRoutes.aiProviders && <Link to={managementRoutes.aiProviders} className={`nav-item ${isActive(managementRoutes.aiProviders) ? 'active' : ''}`} title={(t as any).aiProviders?.title || 'AI Providers'} onClick={closeMobileMenu}><span className="nav-icon"><Icon name="robot" size={20} /></span>{!isCollapsed && <span>{(t as any).aiProviders?.title || 'AI Providers'}</span>}</Link>}
                                {managementRoutes.activityLogs && <Link to={managementRoutes.activityLogs} className={`nav-item ${isActive(managementRoutes.activityLogs) ? 'active' : ''}`} title={(t as any).sidebar?.activityLogs || 'Aktivite Günlükleri'} onClick={closeMobileMenu}><span className="nav-icon"><Icon name="schedule" size={20} /></span>{!isCollapsed && <span>{(t as any).sidebar?.activityLogs || 'Aktivite Günlükleri'}</span>}</Link>}
                        </SidebarSection>
                    )}
                    {canViewSettings && (
                        <SidebarSection id={`${sectionIdPrefix}-settings`} label={t.sidebar.settings}
                            isCollapsed={isCollapsed} isOpen={isSettingsOpen} hasActiveChild={hasActiveSettingsItem()}
                            onToggle={handleSettingsToggle} icon={<Icon name="settings" size={20} />}
                            arrow={isSettingsOpen ? <Icon name="expandMore" size={16} /> : <Icon name="chevronRight" size={16} />}>
                                {canViewSystemSettings && settingsRoutes.system && <Link to={settingsRoutes.system} className={`nav-item ${location.pathname.includes(settingsRoutes.system) ? 'active' : ''}`} title={t.settings.systemSettings} onClick={closeMobileMenu}><span className="nav-icon"><Icon name="settings" size={20} /></span>{!isCollapsed && <span>{t.settings.systemSettings}</span>}</Link>}
                                {!personalSettingsTabs && canViewUserSettings && settingsRoutes.security && <Link to={settingsRoutes.security} className={`nav-item ${location.pathname.includes(settingsRoutes.security) ? 'active' : ''}`} title={t.settings.security} onClick={closeMobileMenu}><span className="nav-icon"><Icon name="lock" size={20} /></span>{!isCollapsed && <span>{t.settings.security}</span>}</Link>}
                                {canViewUserSettings && settingsRoutes.user && <Link to={settingsRoutes.user} className={`nav-item ${location.pathname.includes(settingsRoutes.user) ? 'active' : ''}`} title={t.settings.userSettings} onClick={closeMobileMenu}><span className="nav-icon"><Icon name="person" size={20} /></span>{!isCollapsed && <span>{t.settings.userSettings}</span>}</Link>}
                                {canAccessPermission('customField:read') && settingsRoutes.customFields && <Link to={settingsRoutes.customFields} className={`nav-item ${isActive(settingsRoutes.customFields) ? 'active' : ''}`} title={(t as any).customFields?.title || 'Ek Alanlar'} onClick={closeMobileMenu}><span className="nav-icon"><Icon name="tag" size={20} /></span>{!isCollapsed && <span>{(t as any).customFields?.title || 'Ek Alanlar'}</span>}</Link>}
                                {canAccessPermission('translation:read') && settingsRoutes.translations && <Link to={settingsRoutes.translations} className={`nav-item ${isActive(settingsRoutes.translations) ? 'active' : ''}`} title={t.sidebar.translations} onClick={closeMobileMenu}><span className="nav-icon"><Icon name="language" size={20} /></span>{!isCollapsed && <span>{t.sidebar.translations}</span>}</Link>}
                        </SidebarSection>
                    )}
                </>
            )}
        </nav>
    );
};
